/**
 * Purchase Orders Hub — bookkeeper / CFO surface for AP management.
 *
 * Aggregates all POs across all packets. Supports search, filter by status /
 * vendor, AP aging buckets, soft 3-way match variance flagging, and drill into
 * individual PO detail (invoices, receipts, payments).
 *
 * Soft 3-way match: surfaces variance pills but never blocks payment marking.
 */
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt, Search, FileText, AlertTriangle, CheckCircle2, DollarSign, Building2, Calendar, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { format, differenceInDays } from "date-fns";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import { PO_STATUS_LABEL, PO_STATUS_TONE, type PurchaseOrder, type PurchaseOrderStatus } from "@/data/orderingPacket/types";
import { PurchaseOrderDetail } from "@/components/finance/PurchaseOrderDetail";

const TONE_BADGE: Record<string, string> = {
  muted: "bg-muted text-muted-foreground border-border",
  info: "bg-info/15 text-info border-info/30",
  warning: "bg-warning/15 text-warning border-warning/30",
  success: "bg-success/15 text-success border-success/30",
  destructive: "bg-destructive/15 text-destructive border-destructive/30",
};

function poVariance(po: PurchaseOrder): number {
  // Soft 3-way: PO subtotal vs sum(invoiceAmounts). 0 if no invoices.
  if (po.invoices.length === 0) return 0;
  const invoiced = po.invoices.reduce((s, i) => s + i.invoiceAmount, 0);
  return +(invoiced - po.subtotal).toFixed(2);
}

function poUnpaidAmount(po: PurchaseOrder): number {
  return po.invoices.filter((i) => !i.paid).reduce((s, i) => s + i.invoiceAmount, 0);
}

function poOldestUnpaidDays(po: PurchaseOrder): number | null {
  const unpaid = po.invoices.filter((i) => !i.paid);
  if (unpaid.length === 0) return null;
  const oldest = unpaid.reduce((min, i) => i.invoiceDate < min ? i.invoiceDate : min, unpaid[0].invoiceDate);
  return differenceInDays(new Date(), new Date(oldest));
}

export default function PurchaseOrdersHub() {
  const { getAllPurchaseOrders } = useOrderingPacket();
  const allPOs = getAllPurchaseOrders();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | "all" | "open" | "unpaid" | "variance">("all");
  const [vendorFilter, setVendorFilter] = useState<string>("all");
  const [selectedPOId, setSelectedPOId] = useState<string | null>(null);

  const vendors = useMemo(() => {
    const set = new Set(allPOs.map((p) => p.vendorName));
    return Array.from(set).sort();
  }, [allPOs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allPOs.filter((po) => {
      if (vendorFilter !== "all" && po.vendorName !== vendorFilter) return false;
      if (statusFilter === "open") {
        if (po.status === "paid" || po.status === "closed" || po.status === "cancelled") return false;
      } else if (statusFilter === "unpaid") {
        if (poUnpaidAmount(po) === 0) return false;
      } else if (statusFilter === "variance") {
        if (poVariance(po) === 0) return false;
      } else if (statusFilter !== "all") {
        if (po.status !== statusFilter) return false;
      }
      if (!q) return true;
      return (
        po.poNumber.toLowerCase().includes(q) ||
        po.vendorName.toLowerCase().includes(q) ||
        po.customerName.toLowerCase().includes(q) ||
        po.jobLabel.toLowerCase().includes(q) ||
        (po.vendorOrderNumber ?? "").toLowerCase().includes(q) ||
        po.invoices.some((i) => i.invoiceNumber.toLowerCase().includes(q))
      );
    }).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [allPOs, search, statusFilter, vendorFilter]);

  // KPI rollups
  const kpis = useMemo(() => {
    const open = allPOs.filter((p) => p.status !== "paid" && p.status !== "closed" && p.status !== "cancelled");
    const committed = open.reduce((s, p) => s + p.subtotal, 0);
    const unpaidTotal = allPOs.reduce((s, p) => s + poUnpaidAmount(p), 0);
    const variances = allPOs.filter((p) => poVariance(p) !== 0);
    const aging = { d30: 0, d60: 0, d90: 0 };
    allPOs.forEach((p) => {
      const days = poOldestUnpaidDays(p);
      const amt = poUnpaidAmount(p);
      if (days == null || amt === 0) return;
      if (days >= 90) aging.d90 += amt;
      else if (days >= 60) aging.d60 += amt;
      else if (days >= 30) aging.d30 += amt;
    });
    return { openCount: open.length, committed, unpaidTotal, varianceCount: variances.length, aging };
  }, [allPOs]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight flex items-center gap-2">
            <Receipt className="h-5 w-5 text-primary" /> Purchase Orders
          </h1>
          <p className="text-[12px] text-muted-foreground mt-1">
            Company POs across all jobs · 3-way match (PO ↔ Receipt ↔ Invoice) · Bookkeeper & CFO view
          </p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi icon={<FileText className="h-4 w-4" />} label="Open POs" value={kpis.openCount.toString()} />
        <Kpi icon={<DollarSign className="h-4 w-4" />} label="Committed Spend" value={`$${kpis.committed.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} sub="open + sent + received, not paid" />
        <Kpi icon={<DollarSign className="h-4 w-4" />} label="Unpaid Invoices" value={`$${kpis.unpaidTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} tone={kpis.unpaidTotal > 0 ? "warning" : undefined} />
        <Kpi icon={<AlertTriangle className="h-4 w-4" />} label="3-Way Variances" value={kpis.varianceCount.toString()} tone={kpis.varianceCount > 0 ? "warning" : "success"} sub="invoice ≠ PO subtotal" />
        <Kpi
          icon={<Calendar className="h-4 w-4" />}
          label="AP Aging (>30d)"
          value={`$${(kpis.aging.d30 + kpis.aging.d60 + kpis.aging.d90).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          sub={`30d $${kpis.aging.d30.toLocaleString(undefined, { maximumFractionDigits: 0 })} · 60d $${kpis.aging.d60.toLocaleString(undefined, { maximumFractionDigits: 0 })} · 90d $${kpis.aging.d90.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          tone={kpis.aging.d90 > 0 ? "destructive" : kpis.aging.d60 > 0 ? "warning" : undefined}
        />
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search PO #, vendor, job, customer, invoice #…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-[12px]"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="h-9 w-[180px] text-[12px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="open">Open (not paid/closed)</SelectItem>
              <SelectItem value="unpaid">Has unpaid invoice</SelectItem>
              <SelectItem value="variance">Has 3-way variance</SelectItem>
              {Object.entries(PO_STATUS_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={vendorFilter} onValueChange={setVendorFilter}>
            <SelectTrigger className="h-9 w-[180px] text-[12px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-[11px] text-muted-foreground ml-auto">
            {filtered.length} of {allPOs.length} POs
          </div>
        </div>
      </Card>

      {/* PO table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-wider w-[150px]">PO #</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Vendor</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Job / Customer</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider w-[130px]">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right w-[110px]">Subtotal</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right w-[110px]">Invoiced</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right w-[110px]">Unpaid</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider w-[90px]">Variance</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider w-[90px]">Aging</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-12 text-[12px] text-muted-foreground">
                    No purchase orders match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((po) => {
                  const variance = poVariance(po);
                  const unpaid = poUnpaidAmount(po);
                  const invoiced = po.invoices.reduce((s, i) => s + i.invoiceAmount, 0);
                  const aging = poOldestUnpaidDays(po);
                  const tone = PO_STATUS_TONE[po.status];
                  return (
                    <TableRow
                      key={po.id}
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => setSelectedPOId(po.id)}
                    >
                      <TableCell className="font-mono text-[11px] font-semibold">{po.poNumber}</TableCell>
                      <TableCell className="text-[12px]">
                        <div className="flex items-center gap-1.5">
                          <Building2 className="h-3 w-3 text-muted-foreground" />
                          {po.vendorName}
                        </div>
                        {po.vendorOrderNumber && (
                          <div className="text-[10px] text-muted-foreground font-mono">SO# {po.vendorOrderNumber}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-[12px]">
                        <div className="font-medium">{po.customerName}</div>
                        <div className="text-[10px] text-muted-foreground">{po.jobLabel}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("text-[10px] font-semibold border", TONE_BADGE[tone])}>
                          {PO_STATUS_LABEL[po.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-[12px] font-medium">
                        ${po.subtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-[12px]">
                        {invoiced > 0 ? `$${invoiced.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-[12px]">
                        {unpaid > 0
                          ? <span className="font-semibold text-warning">${unpaid.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                          : po.invoices.length > 0
                            ? <span className="inline-flex items-center gap-1 text-success"><CheckCircle2 className="h-3 w-3" />Paid</span>
                            : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {variance !== 0 ? (
                          <Badge className={cn("text-[10px] font-semibold border tabular-nums",
                            Math.abs(variance) > 50 ? TONE_BADGE.warning : TONE_BADGE.muted)}>
                            <AlertTriangle className="h-2.5 w-2.5 mr-1" />
                            {variance > 0 ? "+" : ""}${variance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </Badge>
                        ) : po.invoices.length > 0 ? (
                          <Badge className={cn("text-[10px] font-semibold border", TONE_BADGE.success)}>
                            <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Match
                          </Badge>
                        ) : <span className="text-[10px] text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {aging != null ? (
                          <span className={cn("text-[11px] font-medium tabular-nums",
                            aging >= 90 ? "text-destructive" :
                            aging >= 60 ? "text-warning" :
                            aging >= 30 ? "text-warning/80" : "text-muted-foreground")}>
                            {aging}d
                          </span>
                        ) : <span className="text-[10px] text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/jobs/${po.jobId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-muted-foreground hover:text-primary"
                          title="Open job"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* PO Detail dialog */}
      <Dialog open={!!selectedPOId} onOpenChange={(o) => !o && setSelectedPOId(null)}>
        <DialogContent className="max-w-[1100px] w-[95vw] h-[90vh] max-h-[90vh] p-0 gap-0 overflow-hidden grid grid-rows-[auto_minmax(0,1fr)]">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
            <DialogTitle className="text-[16px] font-bold">Purchase Order Detail</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            {selectedPOId && <PurchaseOrderDetail poId={selectedPOId} onClose={() => setSelectedPOId(null)} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon, label, value, sub, tone }: {
  icon: React.ReactNode; label: string; value: string; sub?: string;
  tone?: "warning" | "success" | "destructive";
}) {
  const valueClass =
    tone === "warning" ? "text-warning" :
    tone === "success" ? "text-success" :
    tone === "destructive" ? "text-destructive" : "text-foreground";
  return (
    <Card className="p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        {icon}{label}
      </div>
      <div className={cn("text-[18px] font-bold tabular-nums mt-1", valueClass)}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}
